import { Close } from "@carbon/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import PageLoader from "../../../../components/PageLoader";
import FallbackComponent from "../../../../components/fallbackComponent/FallbackComponent";
import { useOrderContext } from "../../../../contexts/OrderContext";
import { SavedProgressReducerActionTypes } from "../../../../contexts/reducers/savedProgressReducer";
import { useNewOrderQueryData } from "../../../../hooks/useNewOrderQueryData";
import { IGeneralInfo } from "../../../../models/generalInfo";
import GeneralInfoForm from "../../components/forms/generalInfoForm/GeneralInfoForm";
import BaseContent from "../BaseContent";

interface GeneralInfoScreenProps {}

export const emptyGeneralInfoFormData: IGeneralInfo = {
  deliveryNote: "",
  deliveryNumber: "",
  documentNumber: "",
  documentType: null,
  location: "",
  locationId: "",
};

const isKeyOfFormData = (key: string): key is keyof IGeneralInfo => {
  return key in emptyGeneralInfoFormData;
};

const GeneralInfoScreen = (props: GeneralInfoScreenProps) => {
  //#region State and variables
  const { t } = useTranslation();
  const { savedProgress, dispatchSavedProgress, steps } = useOrderContext();
  const { queryData, loading, error } = useNewOrderQueryData();

  const [formData, setFormData] = useState<IGeneralInfo>(
    emptyGeneralInfoFormData,
  );
  const [isFormValid, setIsFormValid] = useState(false);
  //#endregion

  const loadSavedProgress = () => {
    if (!savedProgress || !savedProgress.generalInfo) return;

    setFormData(savedProgress.generalInfo);
  };

  const saveProgress = () => {
    dispatchSavedProgress({
      type: SavedProgressReducerActionTypes.SET_PROP,
      property: "generalInfo",
      value: formData,
    });
  };

  const handleLocationChange = (value: string) => {
    if (value === "") {
      // we also need to reset the locationId field
      setFormData({
        ...formData,
        location: "",
        locationId: "",
      });
    } else {
      // we need to set the location field and the locationId field
      setFormData({
        ...formData,
        location: value,
        locationId:
          queryData.locations?.find((location) => location.name === value)
            ?.id || "",
      });
    }
  };

  const onChange = (fieldId: string, value: string) => {
    if (!isKeyOfFormData(fieldId)) {
      console.error(`Invalid fieldId of input: ${fieldId}`);
      return;
    }

    if (fieldId === "location") {
      // we need to handle setting the location field differently
      handleLocationChange(value);
      return;
    }

    setFormData({ ...formData, [fieldId]: value });
  };

  useEffect(() => {
    loadSavedProgress();
  }, []);

  const renderContent = () => {
    if (loading) return <PageLoader />;
    if (error || !queryData.locations)
      return (
        <FallbackComponent
          middleIcon={<Close size={32} />}
          text={`${t("queryError")}. ${t("tryAgainLater")}`}
        />
      );

    return (
      <GeneralInfoForm
        formData={formData}
        onChange={onChange}
        setIsFormValid={(value) => setIsFormValid(value)}
      />
    );
  };

  return (
    <BaseContent
      screenName={"generalInfoScreen"}
      disableNext={!isFormValid}
      proceedButtonText={t("proceedItems")}
      saveProgress={saveProgress}
    >
      <div className="form-component">{renderContent()}</div>
    </BaseContent>
  );
};
export default GeneralInfoScreen;
